import { useEffect, useMemo, useState } from 'react';
import {
    Box, Typography, Chip, Autocomplete, TextField, IconButton,
    Avatar, Stack, Paper, Tooltip, InputAdornment, Badge, Popover,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ClientWorkspace from './ClientWorkspace';
import { getProviderSegmentSummary } from '../../../services/providerService';
import { AVATAR_BG, AVATAR_FG, initials, CONNECTED_HELP_TEXT } from './constants';

const BLUE = '#004df2';
const LIST_WIDTH = 300;

// One provider's whole workspace, inbox-style: a searchable/filterable list
// of every client wired up to it on the left, whichever one is selected gets
// its full workspace (ClientWorkspace: Overview/Schema/Notes) on the right.
// The list collapses so the reading pane can take the full width when you
// just want to focus on one client.

const ProviderOverview = ({
    providerId, providerName, clients, allClients, segmentFlagsDisabled,
    onClientChange, onAddClient, onRemoveClient,
}) => {
    const [selectedId, setSelectedId] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const [search, setSearch] = useState('');
    const [connectedOnly, setConnectedOnly] = useState(false);
    const [notesOnly, setNotesOnly] = useState(false);
    const [segmentsByClient, setSegmentsByClient] = useState({});
    const [flagPopover, setFlagPopover] = useState(null); // { anchorEl, clientId } | null

    // Segment-drift hint data. The on/off switch itself lives in
    // ProviderInfoCard and is passed down as a prop (not fetched here) so
    // flipping it takes effect immediately, no refresh needed. Fetch failure
    // here is silent — it's a nice-to-have hint, not worth erroring the page over.
    useEffect(() => {
        let alive = true;
        setSegmentsByClient({});
        if (!providerId) return undefined;
        getProviderSegmentSummary(providerId)
            .then((summary) => { if (alive) setSegmentsByClient(summary); })
            .catch(() => {});
        return () => { alive = false; };
    }, [providerId]);

    const referenceSegments = useMemo(() => {
        const all = new Set();
        Object.values(segmentsByClient).forEach((names) => names.forEach((n) => all.add(n)));
        return all;
    }, [segmentsByClient]);

    const missingSegments = useMemo(() => {
        if (segmentFlagsDisabled || referenceSegments.size === 0) return {};
        const result = {};
        Object.entries(segmentsByClient).forEach(([clientId, names]) => {
            const have = new Set(names);
            const missing = [...referenceSegments].filter((n) => !have.has(n));
            if (missing.length > 0) result[clientId] = missing;
        });
        return result;
    }, [segmentFlagsDisabled, referenceSegments, segmentsByClient]);

    const existingIds = useMemo(() => new Set(clients.map((c) => c.clientId)), [clients]);
    const addOptions = useMemo(
        () => allClients.filter((c) => !existingIds.has(c.clientId)),
        [allClients, existingIds],
    );

    const filtered = useMemo(() => clients.filter((c) => {
        if (search && !c.clientName?.toLowerCase().includes(search.toLowerCase())) return false;
        if (connectedOnly && !c.isLive) return false;
        if (notesOnly && !c.hasGeneralNotes) return false;
        return true;
    }), [clients, search, connectedOnly, notesOnly]);

    // Whatever's selected stays open even if a filter change hides it from
    // the list — filters are for browsing, not for yanking away what's open.
    // Only fall back to the first filtered client when nothing valid is selected.
    const activeId = useMemo(
        () => (selectedId && clients.some((c) => c.clientId === selectedId) ? selectedId : (filtered[0]?.clientId ?? null)),
        [selectedId, clients, filtered],
    );
    const activeClient = useMemo(() => clients.find((c) => c.clientId === activeId) || null, [clients, activeId]);

    const openFlagPopover = (e, clientId) => {
        e.stopPropagation();
        setFlagPopover({ anchorEl: e.currentTarget, clientId });
    };
    const closeFlagPopover = () => setFlagPopover(null);

    const addClient = (opt) => {
        if (!opt || existingIds.has(opt.clientId)) return;
        onAddClient({
            clientId: opt.clientId, clientName: opt.name,
            source: 'manual', type: '', isLive: false, schema: {},
        });
        setSelectedId(opt.clientId);
    };

    return (
        <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', letterSpacing: 0.3, mb: 1.5 }}>
                CLIENTS ({clients.length})
            </Typography>

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', display: 'flex', minHeight: 480 }}>
                {!collapsed && (
                    <Box sx={{ width: LIST_WIDTH, flexShrink: 0, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <TextField
                                size="small" fullWidth placeholder="Search clients…" value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} /></InputAdornment>,
                                }}
                                sx={{ mb: 1 }}
                            />
                            <Stack direction="row" spacing={0.75}>
                                <Chip
                                    label="Connected" size="small" clickable onClick={() => setConnectedOnly((v) => !v)}
                                    color={connectedOnly ? 'primary' : 'default'} variant={connectedOnly ? 'filled' : 'outlined'}
                                    sx={{ fontSize: 11, height: 22 }}
                                />
                                <Chip
                                    label="Has notes" size="small" clickable onClick={() => setNotesOnly((v) => !v)}
                                    color={notesOnly ? 'primary' : 'default'} variant={notesOnly ? 'filled' : 'outlined'}
                                    sx={{ fontSize: 11, height: 22 }}
                                />
                            </Stack>
                        </Box>

                        <Box sx={{ flex: 1, overflowY: 'auto' }}>
                            {filtered.map((c) => (
                                <Box
                                    key={c.clientId}
                                    onClick={() => setSelectedId(c.clientId)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.25, px: 1.35, py: 1.1, cursor: 'pointer',
                                        bgcolor: activeId === c.clientId ? 'action.selected' : 'transparent',
                                        borderLeft: '3px solid', borderLeftColor: activeId === c.clientId ? BLUE : 'transparent',
                                        borderBottom: '1px solid', borderBottomColor: 'divider',
                                        '&:hover': { bgcolor: 'action.hover' },
                                    }}
                                >
                                    <Avatar sx={{ bgcolor: AVATAR_BG, color: AVATAR_FG, width: 28, height: 28, fontSize: 11, fontWeight: 700 }}>
                                        {initials(c.clientName)}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600 }}>{c.clientName}</Typography>
                                        {c.type && <Typography noWrap sx={{ fontSize: 10.5, color: 'text.secondary' }}>{c.type}</Typography>}
                                    </Box>
                                    {missingSegments[c.clientId] && (
                                        <Tooltip title="Missing segments other clients have — click for the list" arrow placement="top">
                                            <IconButton size="small" onClick={(e) => openFlagPopover(e, c.clientId)} sx={{ p: 0.25 }}>
                                                <Badge badgeContent={missingSegments[c.clientId].length} color="warning" max={99}
                                                    sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}>
                                                    <WarningAmberIcon sx={{ fontSize: 15, color: 'warning.main' }} />
                                                </Badge>
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title={CONNECTED_HELP_TEXT} arrow placement="top">
                                        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: c.isLive ? 'success.main' : 'divider', flexShrink: 0 }} />
                                    </Tooltip>
                                </Box>
                            ))}
                            {filtered.length === 0 && (
                                <Typography sx={{ fontSize: 12, color: 'text.secondary', p: 2 }}>No clients match.</Typography>
                            )}
                        </Box>

                        <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            <Autocomplete
                                options={addOptions}
                                getOptionLabel={(o) => o.name || ''}
                                onChange={(_, val) => addClient(val)}
                                renderInput={(params) => (
                                    <TextField {...params} size="small" placeholder="+ Add client" />
                                )}
                            />
                        </Box>
                    </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <IconButton size="small" onClick={() => setCollapsed((v) => !v)}
                            title={collapsed ? 'Show client list' : 'Collapse client list'}>
                            {collapsed ? <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} /> : <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 18 }} />}
                        </IconButton>
                        {activeClient && (
                            <>
                                <Typography sx={{ fontSize: 14, fontWeight: 700, flex: 1 }}>{activeClient.clientName}</Typography>
                                {missingSegments[activeClient.clientId] && (
                                    <Tooltip title="Missing segments other clients have — click for the list" arrow placement="top">
                                        <IconButton size="small" onClick={(e) => openFlagPopover(e, activeClient.clientId)} sx={{ p: 0.25 }}>
                                            <Badge badgeContent={missingSegments[activeClient.clientId].length} color="warning" max={99}
                                                sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14 } }}>
                                                <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {activeClient.type && <Chip label={activeClient.type} size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
                                <Tooltip title={CONNECTED_HELP_TEXT} arrow placement="top">
                                    {activeClient.isLive ? (
                                        <Chip label="Connected" size="small" color="success" sx={{ height: 20, fontSize: 11 }} />
                                    ) : (
                                        <Chip
                                            label={activeClient.source === 'manual' ? 'Placeholder — not built' : 'Not connected yet'}
                                            size="small" variant="outlined" sx={{ height: 20, fontSize: 11, color: 'text.secondary' }}
                                        />
                                    )}
                                </Tooltip>
                                {activeClient.source === 'manual' && (
                                    <IconButton size="small" title="Remove this client from the provider"
                                        onClick={() => onRemoveClient(activeClient.clientId)}>
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                )}
                            </>
                        )}
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {activeClient ? (
                            <ClientWorkspace providerId={providerId} providerName={providerName} client={activeClient} onChange={onClientChange} />
                        ) : (
                            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                {clients.length === 0
                                    ? 'No clients on record for this provider yet — add one from the list.'
                                    : 'Select a client to see their workspace.'}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Popover
                open={!!flagPopover}
                anchorEl={flagPopover?.anchorEl}
                onClose={closeFlagPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Box sx={{ p: 1.75, maxWidth: 340 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, mb: 1 }}>
                        Missing {missingSegments[flagPopover?.clientId]?.length ?? 0} segment{(missingSegments[flagPopover?.clientId]?.length ?? 0) === 1 ? '' : 's'} other clients have
                    </Typography>
                    <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
                        {(missingSegments[flagPopover?.clientId] || []).map((name) => (
                            <Typography key={name} sx={{ fontSize: 12, py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                {name}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default ProviderOverview;
